/**
 * View Component: FloatingActionButton
 * Bottom-right floating action buttons: back to top, view toggle.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * T-Cron-2026-10-31 20:00 (T126) — 改进清单 #10「无障碍」+ #3「触感反馈」
 * ────────────────────────────────────────────────────────────────────────────
 *
 * 【本轮修的是什么】两个问题,都在这个 70 行的小组件上:
 *
 *   ① 装饰性 SVG 没有 aria-hidden(0/3)
 *      两个按钮都已经有 aria-label("回到顶部" / "切换视图"),
 *      而里面的 <svg> 是一枚**纯装饰**的线条图标(无 title/无 role=img)。
 *      ⇒ 读屏软件的处理是:先念 aria-label("回到顶部"),
 *        再把 <svg> 当作一个无名图形对象追加播报(常见为 "graphic" / "图像")。
 *      ⇒ 用户听到的是「回到顶部, 图像」这种**多余尾巴**;
 *        而且某些读屏会把焦点落在图形上,造成焦点顺序错乱。
 *      ⇒ 正确做法:svg 加 aria-hidden="true" + focusable="false"
 *        (focusable 是给 IE/Edge Legacy 的,加上无害;
 *         本项目 MobileBottomNav / MobileFilterDrawer 的图标已经是这个写法。)
 *      ⇒ 这不是新发明,是**补齐本项目已有的约定**:
 *        PostCard.jsx / PostDetail.jsx / MobileFilterDrawer.jsx
 *        里的装饰性 svg 全都带 aria-hidden="true",
 *        只有本文件(3 个)和 RefreshButton.jsx(2 个)漏了。
 *
 *   ② 没有触感反馈(改进清单 #3)
 *      底部 FAB 是**纯移动端**控件(44x44 圆钮),和 PostCard / Sidebar /
 *      FavoriteBar / InstallButton 一样属于"点了应该有反馈"的按钮,
 *      但唯独它没接 harness/haptics.js 的 buzz()。
 *      ⇒ 接上,和全站保持一致的「尊重 prefers-reduced-motion」口径。
 *      ⇒ 触感永远不能把主流程带崩 —— harness/haptics.js 内部已 try/catch。
 *
 * 【不做什么】
 *   · 不改任何 DOM 结构 / className ⇒ CSS 零影响;
 *   · 不改 SVG 的路径 / 尺寸 ⇒ 视觉零回归;
 *   · 不引入新依赖(buzz 是纯函数,已有 harness 实现)。
 * ========================================================================== */
import { buzz } from '../harness/haptics.js';

function ArrowUpIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <line x1="12" y1="19" x2="12" y2="5"></line>
      <polyline points="5 12 12 5 19 12"></polyline>
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="3" y="3" width="7" height="7"></rect>
      <rect x="14" y="3" width="7" height="7"></rect>
      <rect x="14" y="14" width="7" height="7"></rect>
      <rect x="3" y="14" width="7" height="7"></rect>
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <line x1="8" y1="6" x2="21" y2="6"></line>
      <line x1="8" y1="12" x2="21" y2="12"></line>
      <line x1="8" y1="18" x2="21" y2="18"></line>
      <line x1="3" y1="6" x2="3.01" y2="6"></line>
      <line x1="3" y1="12" x2="3.01" y2="12"></line>
      <line x1="3" y1="18" x2="3.01" y2="18"></line>
    </svg>
  );
}

/**
 * 🔴 T-Cron-2026-11-01 03:30 (T128) — 批量操作条激活时,FAB 让位。
 *
 * 【实测到的 bug(不是推演)】
 *   探针 `scripts/_probe_t128_fab_geom.py` 在 6 个移动端宽度上实测:
 *     w=360 → FAB 与「取消」按钮**重叠 990px²**,且
 *             `document.elementFromPoint(取消按钮中心)` 返回的是
 *             `.fab-btn.visible` ⇒ **「取消」按钮根本点不动**
 *     w=375 → 重叠 742px²,同样点不动
 *     w=390 → 重叠 495px²(按钮被压住一半)
 *     w=414 → 重叠 99px²
 *     w=320 / w=430 → 干净
 *
 * 【根因】
 *   两个元素都是 `position: fixed`,各自锚在视口上:
 *     · `.fab-container`  移动端 `right:14px; bottom:84px`,48/44px 见方
 *     · `.batch-bar`      移动端 `bottom:68px`,T127 改成两行后高约 92px
 *                        ⇒ 它占据视口底部 [68px, 160px] 这一整条带
 *   两者在竖直方向**必然相交**,在右侧又都靠右 ⇒ 重叠。
 *   注意:这不是「FAB 位置算错了」,而是**两个浮层缺少协调机制** ——
 *   T127 把操作条从 1 行改成 2 行(高度 +52px)之后,没回头看 FAB。
 *
 * 【修法:让位,而不是搬家】
 *   批量操作期间,FAB 的「切换视图」其实**用不上**
 *   (用户正忙着勾选卡片,不会想切网格/列表视图),
 *   但它必须**可见**(不消失会让布局跳动)且**不能挡住任何按钮**。
 *   ⇒ 移动端:批量条激活时,FAB 整体上移到操作条顶边之上;
 *     桌面端:操作条在底部居中、FAB 在右下角,本来就不相交 ⇒ 不动。
 *   ⇒ 用 `bottom` 的过渡动画让它平滑滑上去,而不是瞬移。
 *
 * 【为什么不在 CSS 里用 :has() 自动检测】
 *   `:has(.batch-bar)` 在部分 WebView 上支持不稳,而且
 *   「批量条是否可见」本来就是 React 状态 ⇒ 显式传 prop 更可靠、
 *   更好测(探针可以直接断言 className)。
 *
 * 【不做什么】
 *   · 不改 FAB 的尺寸 / 颜色 / 图标 ⇒ 视觉零回归;
 *   · 不改批量操作条本身的布局(它已经换行修好了);
 *   · 不加新的 z-index 叠放来「盖住」—— 那是把 bug 藏起来,
 *     用户依然点不到「取消」。
 */
function FloatingActionButton({ showBackToTop, onBackToTop, viewMode, onToggleView, batchBarActive = false }) {
  // 🔴 触感反馈(改进清单 #3)。
  //   口径与全站一致:harness/haptics.js 的 buzz() 内部已处理
  //   「设备不支持 vibrate」「用户开了 prefers-reduced-motion」两种跳过,
  //   并且 try/catch 吞掉所有异常 ⇒ 触感永远不可能把主流程带崩。
  //   8ms 是本项目其它按钮统一用的轻触感档位(PostCard / FavoriteBar / Sidebar)。
  const handleBackToTop = React.useCallback(() => {
    buzz(8);
    onBackToTop();
  }, [onBackToTop]);

  const handleToggleView = React.useCallback(() => {
    buzz(8);
    onToggleView();
  }, [onToggleView]);

  return (
    <div className={`fab-container ${batchBarActive ? 'fab-above-batch' : ''}`}>
      <button
        className={`fab-btn ${showBackToTop ? 'visible' : ''}`}
        onClick={handleBackToTop}
        aria-label="回到顶部"
        title="回到顶部"
      >
        <ArrowUpIcon />
      </button>
      <button
        className="fab-btn visible"
        onClick={handleToggleView}
        aria-label="切换视图"
        title="切换视图"
      >
        {viewMode === 'grid' ? <ListIcon /> : <GridIcon />}
      </button>
    </div>
  );
}

window.FloatingActionButton = FloatingActionButton;

export default FloatingActionButton;
